import {
    defaultServiceLanguage,
    ServiceLanguage,
    ServiceLanguageMap,
} from "@albertine/i18n";
import { MemberSourceId, MemberSourceIdMap } from "@albertine/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { format, parseISO } from "date-fns";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { emptyToUndefined, formatProfileDateTime } from "@/utils/format.util";
import { AgentPicker } from "../../agent-picker/AgentPicker";
import { FormField } from "../../form-field/FormField";
import { useMemberProfile } from "../MemberProfileContext";
import { OwnedField, ReadOnlyField } from "./FormFields";
import { GroupsPicker } from "./GroupsPicker";
import { FormShell } from "./ProfileOverlayShell";
import type { FormProps } from "./types";

const LANGUAGE_OPTIONS = ServiceLanguage.options.map((id) => ({
    id,
    label: ServiceLanguageMap[id],
}));

const SOURCE_OPTIONS = MemberSourceId.options.map((id) => ({
    id,
    label: MemberSourceIdMap[id],
}));

const manageFormSchema = z.object({
    assignedAgentId: z.string(),
    groupIds: z.array(z.string()),
    language: z.string(),
    source: z.string(),
    additionalSource: z.string(),
});

type ManageFormValues = z.infer<typeof manageFormSchema>;

export function ManageForm({ onClose }: FormProps) {
    const { profile, agents, groups, saveManage } = useMemberProfile();
    const { control, handleSubmit } = useForm<ManageFormValues>({
        resolver: zodResolver(manageFormSchema),
        defaultValues: {
            assignedAgentId: profile.membership.assignedTo.id,
            groupIds: profile.membership.groups.map((group) => group.id),
            language: profile.membership.language ?? defaultServiceLanguage,
            source: profile.membership.source ?? "",
            additionalSource: profile.membership.additionalSourceInfo ?? "",
        },
    });

    function onSubmit(values: ManageFormValues) {
        const source = MemberSourceId.safeParse(values.source);
        const language = ServiceLanguage.safeParse(values.language);

        saveManage(
            {
                assignedAgentId: values.assignedAgentId,
                groupIds: values.groupIds,
                additionalSourceInfo: emptyToUndefined(values.additionalSource),
                ...(language.success ? { serviceLanguage: language.data } : {}),
                ...(source.success ? { source: source.data } : {}),
            },
            onClose,
        );
    }

    return (
        <FormShell
            title="Edit assignment"
            onClose={onClose}
            onSave={handleSubmit(onSubmit)}
        >
            <Controller
                control={control}
                name="assignedAgentId"
                render={({ field }) => (
                    <FormField label="Assignee" htmlFor="assigned-to">
                        <AgentPicker
                            id="assigned-to"
                            items={agents}
                            value={field.value}
                            onChange={(agent) => field.onChange(agent.id)}
                        />
                    </FormField>
                )}
            />
            <Controller
                control={control}
                name="groupIds"
                render={({ field }) => (
                    <FormField label="Groups">
                        <GroupsPicker
                            id="groups"
                            items={groups}
                            value={field.value}
                            onChange={field.onChange}
                        />
                    </FormField>
                )}
            />
            <OwnedField
                id="language"
                label="Language"
                kind="select"
                options={LANGUAGE_OPTIONS}
                control={control}
                name="language"
            />
            <OwnedField
                id="source"
                label="Source"
                kind="select"
                options={SOURCE_OPTIONS}
                control={control}
                name="source"
            />
            <OwnedField
                id="additional-source"
                label="Additional information on source"
                kind="text"
                control={control}
                name="additionalSource"
            />
            <ReadOnlyField label="Tenant" value={profile.membership.tenant} />
            <ReadOnlyField
                label="Member since"
                value={
                    profile.membership.memberSince
                        ? format(parseISO(profile.membership.memberSince), "d MMM yyyy")
                        : undefined
                }
            />
            {profile.membership.lastSeen && (
                <ReadOnlyField
                    label="Last seen"
                    value={formatProfileDateTime(profile.membership.lastSeen)}
                />
            )}
        </FormShell>
    );
}
